import React, { useState, useRef, useEffect } from "react";
import axios from "axios";

const API_BASE = "http://40.40.69.69:3052";

/*--------API---------*/

export default function TwoFactorPage({ email, onVerified, onBack }) {
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resent, setResent] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (idx, val) => {
    const digit = val.replace(/\D/g, "").slice(-1);
    const next = [...code];
    next[idx] = digit;
    setCode(next);
    setError("");

    if (digit && idx < 5) {
      inputRefs.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !code[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
    if (e.key === "Enter" && code.every(Boolean)) {
      handleVerify();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (!pasted) return;
    const next = [...code];
    pasted.split("").forEach((d, i) => {
      next[i] = d;
    });
    setCode(next);
    const focusIdx = Math.min(pasted.length, 5);
    inputRefs.current[focusIdx]?.focus();
  };

  const handleVerify = async () => {
    const otp = code.join("");
    if (otp.length < 6) {
      setError("Please enter the full 6-digit code.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/api/v1/amis/user/verify`, {
        email_address: email,
        otp,
      });

      if (onVerified) onVerified();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Verification failed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setResent(false);
    try {
      await axios.post(`${API_BASE}/api/v1/amis/user/resend-verification`, {
        email_address: email,
      });
      setResent(true);
      setCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resend code.");
    }
  };

  return (
    <div className="lp-root">
      {/* ── Left branding panel ── */}
      <div className="lp-brand">
        <div className="lp-brand-glow" />
        <div className="lp-brand-logo">🔥</div>
        <h1 className="lp-brand-heading">
          Seritonx CRM.
          <br />
          <span>Built to perform.</span>
        </h1>
        <div className="lp-brand-stats">
          <div className="lp-stat">
            <span className="lp-stat-val">Order</span>
            <span className="lp-stat-lbl">Real Time Quotes</span>
          </div>
          <div className="lp-stat">
            <span className="lp-stat-val">Live</span>
            <span className="lp-stat-lbl">Reporting</span>
          </div>
          <div className="lp-stat">
            <span className="lp-stat-val">Track</span>
            <span className="lp-stat-lbl">Live Tracking on active Gas</span>
          </div>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="lp-form-panel">
        <div className="lp-card">
          {/* Header */}
          <div className="lp-card-header">
            <p className="lp-eyebrow">Two-factor authentication</p>
            <h2 className="lp-card-title">Check your email</h2>
            <p className="lp-card-sub">
              We sent a 6-digit verification code to <strong>{email}</strong>.
              Enter it below to activate your account.
            </p>
          </div>

          {/* Error banner */}
          {error && (
            <div className="lp-error" role="alert">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {error}
            </div>
          )}

          {/* Success resend banner */}
          {resent && !error && (
            <div className="lp-success" role="status">
              ✓ A new code has been sent to {email}.
            </div>
          )}

          {/* OTP inputs */}
          <div className="lp-form">
            <div className="lp-otp-wrap" onPaste={handlePaste}>
              {code.map((digit, idx) => (
                <input
                  key={idx}
                  ref={(el) => (inputRefs.current[idx] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(idx, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(idx, e)}
                  className={`lp-otp-cell${digit ? " lp-otp-filled" : ""}`}
                  autoComplete="one-time-code"
                  aria-label={`Digit ${idx + 1}`}
                />
              ))}
            </div>

            <button
              onClick={handleVerify}
              disabled={loading || code.some((d) => !d)}
              className="lp-btn"
              style={{ marginTop: "8px" }}
            >
              {loading && <span className="lp-spinner" />}
              {loading ? "Verifying…" : "Verify & Continue"}
            </button>
          </div>

          {/* Footer actions */}
          <div
            className="lp-switch"
            style={{ flexDirection: "column", gap: "6px" }}
          >
            <div>
              <span className="lp-switch-text">Didn't receive a code? </span>
              <button className="lp-switch-btn" onClick={handleResend}>
                Resend code
              </button>
            </div>
            <div>
              <button className="back-btn" onClick={onBack}>
                ← Back to sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
